import { graphql } from '@eluve/graphql.tada';

// Fragments

export const llmModelPromptTemplatesDetailsFragment = graphql(`
  fragment LlmModelPromptTemplatesDetails on LlmModelPromptTemplates @_unmask {
    __typename
    id
    isDefault
    isActive
    modelArgsId
    promptTemplateId
    model_args {
      __typename
      id
      modelType
      name
      args
    }
  }
`);

export const promptTemplateModelsFragment = graphql(
  `
    fragment PromptTemplateModels on PromptTemplates @_unmask {
      __typename
      id
      prompt_models(where: { isBundle: { _eq: false } }) {
        ...LlmModelPromptTemplatesDetails
      }
    }
  `,
  [llmModelPromptTemplatesDetailsFragment],
);

export const promptTemplateSettingsFragment = graphql(`
  fragment PromptTemplateSettings on PromptTemplates @_unmask {
    __typename
    outputType
    isActive
    isCurrentDefault
    userSettingsAggregate {
      __typename
      aggregate {
        count
      }
    }
  }
`);

export const promptTemplateVariantFragment = graphql(`
  fragment PromptTemplateVariant on PromptTemplateVariants @_unmask {
    __typename
    promptTemplateId
    variantNumber
    template
    isCurrentDefault
    variantNotes
    createdAt
    creator {
      __typename
      id
      firstName
      lastName
    }
  }
`);

export const promptTemplateVariantsFragment = graphql(
  `
    fragment PromptTemplateVariants on PromptTemplates {
      __typename
      id
      prompt_template_variants(orderBy: { variantNumber: DESC }) {
        ...PromptTemplateVariant
      }
    }
  `,
  [promptTemplateVariantFragment],
);

// Operations

export const getExistingPromptTemplateQuery = graphql(
  `
    query getExistingPromptTemplate($id: uuid!) {
      promptTemplatesByPk(id: $id) {
        __typename
        id
        name
        description
        outputType
        prompt_template_variants(orderBy: { variantNumber: DESC }) {
          ...PromptTemplateVariant
        }
      }
    }
  `,
  [promptTemplateVariantFragment],
);

export const setDefaultPromptTemplateVariantMutation = graphql(`
  mutation setDefaultPromptTemplateVariant(
    $promptTemplateId: uuid!
    $variantNumber: numeric!
  ) {
    updatePromptTemplateVariants(
      where: {
        _and: {
          isCurrentDefault: { _eq: true }
          promptTemplateId: { _eq: $promptTemplateId }
        }
      }
      _set: { isCurrentDefault: false }
    ) {
      returning {
        __typename
        promptTemplateId
        variantNumber
        isCurrentDefault
      }
    }
    updatePromptTemplateVariantsByPk(
      pkColumns: {
        promptTemplateId: $promptTemplateId
        variantNumber: $variantNumber
      }
      _set: { isCurrentDefault: true }
    ) {
      __typename
      promptTemplateId
      variantNumber
      isCurrentDefault
    }
  }
`);

export const createPromptTemplateVariantMutation = graphql(
  `
    mutation createPromptTemplateVariant(
      $promptTemplateId: uuid!
      $template: jsonb!
      $variantNotes: String
    ) {
      insertPromptTemplateVariantsOne(
        object: {
          promptTemplateId: $promptTemplateId
          template: $template
          variantNotes: $variantNotes
        }
      ) {
        ...PromptTemplateVariant
      }
    }
  `,
  [promptTemplateVariantFragment],
);

export const updatePromptTemplateMutation = graphql(`
  mutation updatePromptTemplate(
    $id: uuid!
    $name: String
    $description: String
  ) {
    updatePromptTemplatesByPk(
      pkColumns: { id: $id }
      _set: { name: $name, description: $description }
    ) {
      __typename
      id
      name
      description
    }
  }
`);

export const allModeArgsQuery = graphql(`
  query getAllModeArgs {
    llmModelArgs {
      __typename
      id
      name
      modelType
    }
  }
`);

/**
 * One query for the prompt and output template global admin list pages.
 * Returns every active bundle still held open by an enabled tenant bundle,
 * tagged with each FK so a page can group the results by whichever template id
 * its rows key on. Fetched once per page (not per row) to avoid an N+1 across
 * the table — see `groupGlobalBlockersByFk`.
 *
 * Scoped to enabled-tenant bundles (ENG-3634), matching the DB triggers that
 * guard the prompt/output template toggles. The models page uses
 * `getModelArgsBundleBlockersQuery` instead — its API guard blocks on any
 * active bundle.
 */
export const getActiveBundleBlockersQuery = graphql(`
  query getActiveBundleBlockers {
    llmModelPromptTemplates(
      where: {
        isBundle: { _eq: true }
        isActive: { _eq: true }
        tenant_template_bundles: { isEnabled: { _eq: true } }
      }
    ) {
      __typename
      id
      displayName
      promptTemplateId
      outputTemplateId
      modelArgsId
      tenantTemplateBundlesAggregate(where: { isEnabled: { _eq: true } }) {
        __typename
        aggregate {
          count
        }
      }
    }
  }
`);

/**
 * Blockers for the models list page: every active bundle (is_bundle = true,
 * is_active = true), keyed by model_args_id via `groupGlobalBlockersByFk`.
 *
 * Scope note (ENG-4409): unlike `getActiveBundleBlockersQuery`, this
 * intentionally does NOT filter on tenant_template_bundles.isEnabled, so a
 * model args with an active bundle blocks deactivation even when that bundle
 * is enabled for zero organizations — matching the API guard in
 * `deactivateModelArgs`, which blocks on any active bundle. The aggregate
 * below still counts only enabled tenants, as blast-radius context.
 */
export const getModelArgsBundleBlockersQuery = graphql(`
  query getModelArgsBundleBlockers {
    llmModelPromptTemplates(
      where: { isBundle: { _eq: true }, isActive: { _eq: true } }
    ) {
      __typename
      id
      displayName
      promptTemplateId
      outputTemplateId
      modelArgsId
      tenantTemplateBundlesAggregate(where: { isEnabled: { _eq: true } }) {
        __typename
        aggregate {
          count
        }
      }
    }
  }
`);

export const searchPromptTemplatesQuery = graphql(
  `
    query getPromptTemplates(
      $filter: PromptTemplatesBoolExp
      $offset: Int
      $orderBy: [PromptTemplatesOrderBy!]
      $limit: Int
    ) {
      promptTemplates(
        where: $filter
        offset: $offset
        limit: $limit
        orderBy: $orderBy
      ) {
        __typename
        id
        name
        outputType
        createdAt
        prompt_template_variants {
          ...PromptTemplateVariant
        }
        prompt_models(where: { isBundle: { _eq: false } }) {
          __typename
          id
          isDefault
          isActive
          model_args {
            __typename
            id
            name
            modelType
            args
          }
        }

        metrics {
          __typename
          variantCount
          userDefaultCount
          totalFeedbackCount
          averageRating
        }

        ...PromptTemplateSettings
      }
      promptTemplatesAggregate(where: $filter) {
        __typename
        aggregate {
          __typename
          count
        }
      }
    }
  `,
  [promptTemplateSettingsFragment, promptTemplateVariantFragment],
);
